import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { onMounted, onBeforeUnmount, ref, computed, watch } from "vue";
import { columns, options } from "@/helpers/courseTableHelper";

export default function useCourseTable() {
  const router = useRouter();
  const store = useStore();

  const isPageChangeEnabled = ref(true);
  const selected_rows = ref([]);
  const searchTerm = ref("");
  const sortOrder = ref({ key: "id", direction: "desc" });

  const showConfirmModal = ref(false);
  const confirmModalTitle = ref(
    `Delete Course${selected_rows.value.length > 1 ? "s" : ""}`
  );
  const confirmModalMessage = ref("");

  const currentPage = ref(store.getters["courses/getCurrentPage"]);
  const pageSize = ref(store.getters["courses/getPerPage"]);
  const totalRecords = ref(computed(() => store.getters["courses/getTotal"]));
  const tableData = ref(computed(() => store.getters["courses/getCourses"]));
  const isDeleted = ref(computed(() => store.getters["courses/getIsDeleted"]));
  const isCreated = ref(computed(() => store.getters["courses/getIsCreated"]));
  // const message = ref(computed(() => store.getters["courses/getMessage"]));
  // TODO: Show success message, refresh table or show error message

  watch(isDeleted, (newValue) => {
    if (newValue === true) {
      fetchData();
      store.dispatch("courses/clearIsDeleted");
    }
  });

  watch(isCreated, (newValue) => {
    if (newValue === true) {
      fetchData();
      store.dispatch("courses/clearIsCreated");
    }
  });

  const isLoading = computed(() => store.getters["courses/isLoading"]);

  const fetchData = async () => {
    const payload = {
      page: currentPage.value,
      pageSize: pageSize.value,
      searchTerm: searchTerm.value,
      sortOrder: sortOrder.value,
    };
    store.dispatch("courses/fetchCourses", payload);
  };

  const tableOptions = {
    ...options,
    pageLength: pageSize.value,
    deferLoading: totalRecords.value,
    ajax: (data, callback) => {
      currentPage.value = data.start / data.length + 1;
      !isLoading.value &&
        callback({
          draw: data.draw,
          recordsTotal: totalRecords.value,
          recordsFiltered: totalRecords.value,
          data: tableData.value,
        });
    },
  };

  onMounted(async () => {
    document.addEventListener("click", handleClick);
    fetchData();
  });

  onBeforeUnmount(() => {
    document.removeEventListener("click", handleClick);
  });

  const handleClick = (event) => {
    if (event.target && event.target.classList.contains("view-course-btn")) {
      const courseData = event.target.getAttribute("data");
      const course = JSON.parse(courseData.replace(/&quot;/g, '"'));
      viewCourse(course);
    }
  };

  const onSelect = ({ dt, type }) => {
    if (type === "row") {
      selected_rows.value = dt.rows({ selected: true }).data().toArray();
    }
  };

  const onDeselect = ({ dt, type }) => {
    if (type === "row") {
      selected_rows.value = dt.rows({ selected: true }).data().toArray();
    }
  };

  const handleLengthChange = ({ dt }) => {
    const newLength = dt._iDisplayLength;
    currentPage.value = 1;
    pageSize.value = newLength;
    fetchData();
    isPageChangeEnabled.value = false;
    dt.api.page(currentPage.value - 1).draw(false);
    isPageChangeEnabled.value = true;
  };

  const onPageChange = ({ event }) => {
    if (!isPageChangeEnabled.value) return;
    const info = event.dt.page.info();
    currentPage.value = info.page + 1;
    fetchData();
  };

  const handleSearch = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.search === "function") {
      const searchValue = dataTable.search();
      if (searchTerm.value !== searchValue) {
        searchTerm.value = searchValue;
        currentPage.value = 1;
        fetchData();
        isPageChangeEnabled.value = false;
        dt.api.page(currentPage.value - 1).draw(false);
        isPageChangeEnabled.value = true;
      }
    }
  };

  const sortKeyValuePair = (columnIdx, direction) => {
    if (!direction) return {};
    else if (columnIdx === 1) return { key: "name", direction };
    else if (columnIdx === 2) return { key: "url", direction };
    else if (columnIdx === 3) return { key: "web_branch", direction };
    else if (columnIdx === 4) return { key: "server_branch", direction };
    else if (columnIdx === 5) return { key: "holes", direction };
    else if (columnIdx === 6) return { key: "tee_provider", direction };
    return {};
  };

  const handleSortChange = ({ dt }) => {
    const dataTable = dt.api;
    if (dataTable && typeof dataTable.order === "function") {
      const order = dataTable.order();
      if (!order || order.length === 0) {
        return;
      }

      const columnIdx = order[0][0];
      const direction = order[0][1];
      const sortValue = sortKeyValuePair(columnIdx, direction);

      if (
        JSON.stringify(sortOrder.value) !== JSON.stringify(sortValue) &&
        Object.keys(sortValue).length > 0
      ) {
        sortOrder.value = sortValue;
        currentPage.value = 1;
        fetchData();
        isPageChangeEnabled.value = false;
        dt.api.page(currentPage.value - 1).draw(false);
        isPageChangeEnabled.value = true;
      }
    }
  };

  const viewCourse = (course) => {
    router.push({ name: "course", params: { id: course.id } });
  };

  const openDeleteConfirmation = () => {
    if (selected_rows.value.length === 0) {
      store.dispatch("toastNotification/displayToast", {
        message: "Please select at least one course to delete",
        type: "warning",
        duration: 1500,
      });
      return;
    }

    confirmModalMessage.value = `Are you sure you want to delete ${
      selected_rows.value.length
    } selected course${selected_rows.value.length > 1 ? "s" : ""}?`;
    showConfirmModal.value = true;
  };

  const deleteCourses = async () => {
    const payload = selected_rows.value.map((row) => row.id);
    try {
      await store.dispatch("courses/deleteCourses", payload);
      store.dispatch("toastNotification/displayToast", {
        message: `${payload.length} courses deleted successfully`,
        type: "success",
        duration: 1500,
      });
    } catch (error) {
      store.dispatch("toastNotification/displayToast", {
        message: `Failed to delete ${payload.length} course${
          payload.length > 1 ? "s" : ""
        }`,
        type: "error",
        duration: 1500,
      });
    }
  };

  return {
    columns,
    tableData,
    tableOptions,
    handleSearch,
    handleSortChange,
    onSelect,
    onDeselect,
    handleLengthChange,
    onPageChange,
    deleteCourses,
    showConfirmModal,
    confirmModalTitle,
    confirmModalMessage,
    openDeleteConfirmation,
  };
}
